import {
  Dialog,
  DialogContent,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Box,
  Typography,
  useTheme,
  Zoom,
} from '@mui/material';
import { useState } from 'react';

import {
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  ReceiptLong as ReceiptLongIcon,
  PictureAsPdf as PictureAsPdfIcon,
  PointOfSale as PointOfSaleIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

interface IDialogVisualizarVendaProps {
  open: boolean;
  pdfUrl: string | null;
  idVenda: string;
  onClose: () => void;
  onEmitirNota?: () => void;
  onGerarImpressaoCaixa: (id: string) => Promise<void>;
  onGerarImpressaoTermica: (id: string) => Promise<void>;
  // Nova prop para abrir o dialog de NFC-e
  onAbrirDialogNfce?: (idVenda: string) => void;
  // Indica que a NFC-e dessa venda já foi emitida com sucesso
  notaEmitidaComSucesso?: boolean;
}

export const DialogVisualizarVenda = ({
  open,
  pdfUrl,
  idVenda,
  onClose,
  onEmitirNota,
  onGerarImpressaoCaixa,
  onGerarImpressaoTermica,
  onAbrirDialogNfce, // Nova prop
  notaEmitidaComSucesso = false,
}: IDialogVisualizarVendaProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const bgSubtle = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)';

  const [tipoImpressao, setTipoImpressao] = useState<'caixa' | 'termica'>('caixa');

  const imprimir = () => {
    if (!pdfUrl) return;

    const win = window.open(pdfUrl, '_blank');

    setTimeout(() => {
      win?.print();
    }, 500);
  };

  const downloadPdf = () => {
    if (!pdfUrl) return;

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'venda.pdf';
    link.click();
  };

  // Função para lidar com a emissão da nota
  const handleEmitirNota = () => {
    // Mantém o dialog de visualização aberto, a NFC-e abre por cima
    // Abre o dialog de NFC-e
    if (onAbrirDialogNfce) {
      onAbrirDialogNfce(idVenda);
    }
    // Chama o callback onEmitirNota se existir
    if (onEmitirNota) {
      onEmitirNota();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 220 }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          background: theme.palette.background.default,
          maxHeight: '92vh',
          height: '92vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: `0.5px solid ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PictureAsPdfIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
              Visualização da venda
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Confira o documento antes de imprimir ou emitir a nota
            </Typography>
          </Box>
        </Box>

        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── BARRA DE AÇÕES ── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          borderBottom: `0.5px solid ${borderColor}`,
          bgcolor: bgSubtle,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            border: `0.5px solid ${borderColor}`,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <Button
            size="small"
            startIcon={<PointOfSaleIcon fontSize="small" />}
            onClick={async () => {
              setTipoImpressao('caixa');
              await onGerarImpressaoCaixa(idVenda);
            }}
            sx={{
              borderRadius: 0,
              px: 1.5,
              color: tipoImpressao === 'caixa' ? '#fff' : 'text.secondary',
              bgcolor: tipoImpressao === 'caixa' ? 'primary.main' : 'transparent',
              '&:hover': {
                bgcolor: tipoImpressao === 'caixa' ? 'primary.dark' : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              },
            }}
          >
            Caixa
          </Button>
          <Button
            size="small"
            startIcon={<ReceiptIcon fontSize="small" />}
            onClick={async () => {
              setTipoImpressao('termica');
              await onGerarImpressaoTermica(idVenda);
            }}
            sx={{
              borderRadius: 0,
              px: 1.5,
              borderLeft: `0.5px solid ${borderColor}`,
              color: tipoImpressao === 'termica' ? '#fff' : 'text.secondary',
              bgcolor: tipoImpressao === 'termica' ? 'primary.main' : 'transparent',
              '&:hover': {
                bgcolor: tipoImpressao === 'termica' ? 'primary.dark' : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              },
            }}
          >
            Térmica
          </Button>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Imprimir">
            <IconButton
              onClick={imprimir}
              size="small"
              sx={{
                border: `0.5px solid ${borderColor}`,
                color: 'text.secondary',
                '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
              }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Baixar PDF">
            <IconButton
              onClick={downloadPdf}
              size="small"
              sx={{
                border: `0.5px solid ${borderColor}`,
                color: 'text.secondary',
                '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={notaEmitidaComSucesso ? 'NFC-e já emitida para esta venda' : 'Emitir NFC-e'}>
            <span>
              <Button
                variant="contained"
                size="small"
                disabled={notaEmitidaComSucesso}
                startIcon={notaEmitidaComSucesso ? <CheckCircleIcon fontSize="small" /> : <ReceiptLongIcon fontSize="small" />}
                onClick={handleEmitirNota}
                sx={{
                  bgcolor: notaEmitidaComSucesso ? 'success.main' : 'primary.main',
                  '&:hover': { bgcolor: notaEmitidaComSucesso ? 'success.main' : 'primary.dark' },
                  '&.Mui-disabled': {
                    bgcolor: 'success.main',
                    color: '#fff',
                    opacity: 0.85,
                  },
                }}
              >
                {notaEmitidaComSucesso ? 'Nota emitida com sucesso' : 'Emitir Nota'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* ── CONTEÚDO: PDF ── */}
      <DialogContent
        sx={{
          p: { xs: 1, sm: 2 },
          flex: 1,
          minHeight: 0,
          display: 'flex',
          bgcolor: bgSubtle,
          overflow: 'hidden',
        }}
      >
        {pdfUrl ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              borderRadius: '10px',
              overflow: 'hidden',
              border: `0.5px solid ${borderColor}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              bgcolor: '#fff',
            }}
          >
            <iframe
              src={pdfUrl}
              width="100%"
              height="100%"
              style={{ border: 'none', display: 'block' }}
              title="PDF Venda"
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              borderRadius: '10px',
              border: `0.5px solid ${borderColor}`,
              color: 'text.disabled',
            }}
          >
            <PictureAsPdfIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography variant="body2" color="text.disabled">
              Gerando o documento...
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* ── FOOTER ── */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: `0.5px solid ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: theme.palette.background.paper,
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.disabled" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <kbd style={{
            padding: '2px 6px',
            backgroundColor: isDark ? '#333' : '#f0f0f0',
            borderRadius: 3,
            fontSize: '0.7rem',
            fontFamily: 'monospace',
            border: `0.5px solid ${borderColor}`,
          }}>ESC</kbd>
          fecha a janela
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{ borderColor, color: 'text.secondary' }}
        >
          Fechar
        </Button>
      </Box>
    </Dialog>
  );
};
